import type {Elements, IContentItem} from '@kontent-ai/delivery-sdk'
import {useState, createContext, useContext, useEffect} from 'react'
import {clsx} from 'clsx'
import type {Block} from '@/services/kontent-ai/types'
import {getBrandColorClasses} from '@/products/pdp/utils/brand-colors'
import {RichTextRenderer} from '@/products/flexible-web-toolkit/components/rich-text-renderer'
import {colorWithFallback} from '@/utilities/hooks/color-with-fallback'

// Global state for automatic accordion grouping
const globalAccordionState = {
	openAccordionId: null as string | null,
	listeners: new Set<(id: string | null) => void>(),

	setOpenAccordion(id: string | null) {
		this.openAccordionId = id
		this.listeners.forEach((listener) => {
			listener(id)
		})
	},

	subscribe(listener: (id: string | null) => void) {
		this.listeners.add(listener)
		return () => this.listeners.delete(listener)
	},
}

// Accordion Group Context for managing "only one open at a time" behavior
interface AccordionGroupContextType {
	openAccordionId: string | null
	setOpenAccordionId: (id: string | null) => void
}

const AccordionGroupContext = createContext<AccordionGroupContextType | null>(
	null
)

export type PDPHeaderAccordionContentItem = IContentItem<{
	groupTitle: Elements.TextElement
	descriptionRichText: Elements.RichTextElement
}>

export const PDPHeaderAccordionBlock: Block<PDPHeaderAccordionContentItem> = ({
	block,
	page,
	BlockMapper,
	globalConfig,
}) => {
	const groupTitle = block.elements.groupTitle
	const descriptionRichText = block.elements.descriptionRichText
	const accordionId = block.system.id
	const [globalIsOpen, setGlobalIsOpen] = useState(false)
	const accordionGroupContext = useContext(AccordionGroupContext)

	useEffect(() => {
		const unsubscribe = globalAccordionState.subscribe((openId) => {
			setGlobalIsOpen(openId === accordionId)
		})
		return () => {
			unsubscribe()
		}
	}, [accordionId])

	const isOpen = accordionGroupContext
		? accordionGroupContext.openAccordionId === accordionId
		: globalIsOpen

	const toggleAccordion = accordionGroupContext
		? () => {
				accordionGroupContext.setOpenAccordionId(
					isOpen ? null : accordionId
				)
			}
		: () => {
				const newState = !globalIsOpen
				globalAccordionState.setOpenAccordion(
					newState ? accordionId : null
				)
			}

	const {colorId} = colorWithFallback({
		pageConfig: page.elements.config[0],
	})
	const brandColorClasses = getBrandColorClasses(colorId)

	if (!groupTitle && !descriptionRichText.value) {
		return null
	}

	return (
		<div
			className="pdp-accordion mx-auto first:border-t-2 first:border-gray-200"
			data-kontent-element-codename="pdp_header_accordion"
		>
			<div className="border-b-2 border-gray-200 bg-white">
				{/* Accordion Header */}
				<button
					aria-controls={`accordion-content-${accordionId}`}
					aria-expanded={isOpen}
					className={clsx(
						'hover:bg-gray-50 flex w-full items-center justify-between bg-white px-4 py-4 text-left transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500'
					)}
					onClick={toggleAccordion}
					type="button"
				>
					<span className="text-gray-900 text-lg font-semibold">
						{groupTitle}
					</span>
					<svg
						className={clsx(
							`h-6 w-6 transition-transform duration-200 ${brandColorClasses.text}`,
							isOpen ? 'rotate-180' : 'rotate-0'
						)}
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							d="M19 9l-7 7-7-7"
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth={2}
						/>
					</svg>
				</button>

				{/* Accordion Content */}
				{isOpen ? (
					<div
						className="bg-white pb-4"
						id={`accordion-content-${accordionId}`}
					>
						<div className="text-gray-900 px-4 text-base leading-relaxed">
							<RichTextRenderer
								BlockMapper={BlockMapper}
								element={descriptionRichText}
								globalConfig={globalConfig}
								page={page}
							/>
						</div>
					</div>
				) : null}
			</div>
		</div>
	)
}
